// selectToolActivity — the pure selector from the live `AgentActivityStep[]`
// tool-call stream to the `ToolActivityGroup[]` shape `ToolActivityView`
// wants (dogfood F18's "one selector, upstream of the component" seam).
//
// GROUPING: consecutive steps that share the SAME tool name collapse under
// one skill-call header — three `read_source` calls in a row become one
// group of 3, matching the reference behaviour ("many web searches → one
// 'Searched the web'"). A lone call is a group of one; `ToolActivityView`
// already renders a size-1 group as a plain row with no extra chrome, so
// this selector doesn't special-case it.
//
// WHAT THIS SELECTOR FILLS IN (F124): a done step carries a typed outcome
// (`ToolStepOutcome`). Each resource its `ToolCallDetail` names becomes a
// `ToolCallResource` (title + kind chip; never the internal id, F125); a
// source / material / package is openable, its click handed to
// `actions.openResource` (the chat module's `requested_resource_open` intent
// → the right panel). A package also gets `actions.downloadExport`. A
// failure maps its CATEGORY to a short localized reason; the raw `detail`
// string is agent-facing English and never shown. Steps without detail
// (legacy logs, tools with nothing to point at) keep the "No details"
// fallback (PrimaryLine, dogfood F18c).
//
// READ LOCATORS: a read whose detail names WHICH part it read becomes a
// read item (`readSpan`); consecutive reads of the same source merge into
// one, ranges sorted and merged, each a link opening the source there.
//
// AGENT MESSAGES (todo_agent_steps_as_messages): a row reads as intent +
// result. The intent line is the first-person verb (`firstPersonStep`, in
// the view) with a touched/cited resource as its target chip ("Jag läste
// källan [Algorithm Design]"). A RESULT line (`resultLine`) is added only
// where the detail says more than its target: a listing's count ("Hittade 6
// källor") and an export's readiness. A failure's result is its reason.
//
// The per-tool verb pair (present/past) comes from `toolStepLabels`,
// as `MessageDescriptor`s — this selector stays free of an `i18n` instance
// (same discipline as `toolStepLabels` itself, whose header note explains
// why), so it is a plain, unit-testable fold. The caller (a component that
// already has `useLingui()`) resolves the verbs and the failure reason to
// strings via `i18n._` before handing the model to `ToolActivityView`.

export type { ToolActivityActions, ToolCallItemSource };
export { selectToolActivity };

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { pageFocusTarget } from "../../Navigation/focusTarget.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import { type ToolStepVerbMessages, toolStepVerb } from "../toolStepLabels.js";
import type {
    AgentActivityStep,
    ToolCallDetail,
    ToolFailure,
    ToolReadLocator,
    ToolRecordedClaim,
    ToolResourceLink,
} from "../types.js";
import type {
    ToolCallItem,
    ToolCallReadRange,
    ToolCallReadSpan,
    ToolCallResource,
    ToolCallStatus,
} from "./ToolActivityView.js";

// The intents a row's links map to. Absent ⇒ those links render inert (a
// surface that wires no opener never shows a dead link).
type ToolActivityActions = {
    // `target`: WHERE in it to open (a read locator's page), when known.
    readonly openResource?: (ref: ResourceRef, target?: FocusTarget) => void;
    readonly downloadExport?: (exportId: string) => void;
    // Re-upload the original file INTO this source (its content is missing).
    readonly reuploadSource?: (sourceId: string, sourceTitle: string) => void;
};

// `ToolCallItem` with its failure reason and result line still
// MessageDescriptors (the caller localizes them, like the verbs).
type ToolCallItemSource = Omit<ToolCallItem, "errorReason" | "resultLine"> & {
    readonly errorReason?: MessageDescriptor;
    readonly resultLine?: MessageDescriptor;
};

// Mirrors `ToolActivityGroup` (ToolActivityView.tsx) with `verb` left as
// MessageDescriptors instead of localized strings — the pre-i18n shape this
// selector hands to its caller.
type ToolActivityGroupSource = {
    readonly id: string;
    readonly verb: ToolStepVerbMessages;
    readonly items: readonly [ToolCallItemSource, ...ToolCallItemSource[]];
};

const stepStatus = (step: AgentActivityStep): ToolCallStatus => {
    switch (step.type) {
        case "tool_running":
            return "running";
        case "tool_done":
            return step.outcome.type === "completed" ? "done" : "failed";
        default:
            return exhaustSwitchCase(step);
    }
};

// A resource link as a row chip: openable when its kind has a home in the
// right panel and an opener is wired; a raw file or a deleted resource stays
// inert text (graceful degradation: still named, nowhere to go).
const toResource = (
    link: ToolResourceLink,
    open: ((ref: ResourceRef) => void) | undefined,
): ToolCallResource => {
    const { id, title } = link;
    const inert: ToolCallResource = { type: "inert", id, title };
    if (open === undefined) return inert;
    switch (link.kind) {
        case "source":
            return {
                type: "openable",
                id,
                title,
                kind: "source",
                onOpen: () => open({ type: "source", id, title }),
            };
        case "material":
            return {
                type: "openable",
                id,
                title,
                kind: "material",
                onOpen: () => open({ type: "material", id, title }),
            };
        case "export":
            return {
                type: "openable",
                id,
                title,
                kind: "package",
                onOpen: () => open({ type: "export", id, title }),
            };
        case "file":
        case "deleted":
            return inert;
        default:
            return exhaustSwitchCase(link.kind);
    }
};

// The resources a detail names, in the tool's order.
const detailLinks = (detail: ToolCallDetail): readonly ToolResourceLink[] => {
    switch (detail.type) {
        case "resource_touched":
            return [detail.resource];
        case "parts_planned":
            return [detail.root];
        case "citation_added":
        case "source_still_processing":
            return [detail.source];
        case "package_exported":
            return [detail.package];
        case "resources_listed":
            return detail.resources;
        case "recorded_claim":
            return detail.backing.map((b) => b.source);
        default:
            return exhaustSwitchCase(detail);
    }
};

// What a listing found, counted by kind when every live hit shares one.
const foundLine = (
    resources: readonly ToolResourceLink[],
): MessageDescriptor => {
    const n = resources.length;
    if (n === 0) return msg`Found nothing`;
    const kinds = new Set(resources.map((r) => r.kind));
    const [only] = kinds;
    const mixed = msg`Found ${plural(n, { one: "# resource", other: "# resources" })}`;
    if (kinds.size > 1 || only === undefined) return mixed;
    switch (only) {
        case "source":
            return msg`Found ${plural(n, { one: "# source", other: "# sources" })}`;
        case "material":
            return msg`Found ${plural(n, { one: "# material", other: "# materials" })}`;
        case "export":
            return msg`Found ${plural(n, { one: "# package", other: "# packages" })}`;
        case "file":
            return msg`Found ${plural(n, { one: "# file", other: "# files" })}`;
        case "deleted":
            return mixed;
        default:
            return exhaustSwitchCase(only);
    }
};

// "Planned N parts": the root is the chip beside it.
const plannedLine = (n: number): MessageDescriptor =>
    msg`${plural(n, { one: "Planned # part", other: "Planned # parts" })}`;

// "Recorded: wording (p. N)": the source is the chip beside it.
const claimLine = (detail: ToolRecordedClaim): MessageDescriptor => {
    const wording = detail.wording;
    const page = detail.backing[0]?.page;
    return page === undefined
        ? msg`Recorded: ${wording}`
        : msg`Recorded: ${wording} (p. ${page})`;
};

// The result line a detail earns beyond its target chip (header note).
const resultLine = (detail: ToolCallDetail): MessageDescriptor | undefined => {
    switch (detail.type) {
        case "resource_touched":
        case "citation_added":
            return undefined;
        case "parts_planned":
            return plannedLine(detail.parts.length);
        case "package_exported":
            return msg`Ready to download`;
        case "resources_listed":
            return foundLine(detail.resources);
        case "source_still_processing":
            return msg`Still reading in the source…`;
        case "recorded_claim":
            return claimLine(detail);
        default:
            return exhaustSwitchCase(detail);
    }
};

// A short, user-facing reason per failure CATEGORY (never the raw detail).
const failureReason = (reason: ToolFailure): MessageDescriptor => {
    switch (reason.type) {
        case "unknown_tool":
            return msg`This tool isn't available.`;
        case "source_content_missing":
            return msg`The source's content is missing.`;
        case "adapter_failed":
            switch (reason.category) {
                case "timeout":
                    return msg`It took too long to respond.`;
                case "network":
                    return msg`The connection was interrupted.`;
                case "provider_busy":
                    return msg`The service was busy. Try again in a minute.`;
                case "permission_denied":
                    return msg`Access was denied.`;
                case "quota_exhausted":
                    return msg`The usage limit was reached.`;
                case "invalid_arguments":
                    return msg`The assistant's request was invalid.`;
                case "context_window_exceeded":
                    return msg`There was too much content to handle at once.`;
                case "source_content_missing":
                    return msg`The source's content is missing.`;
                case "adapter_crashed":
                    return msg`The tool stopped unexpectedly.`;
                case "other":
                    return msg`Something went wrong.`;
                default:
                    return exhaustSwitchCase(reason.category);
            }
        default:
            return exhaustSwitchCase(reason);
    }
};

// One call: its status plus, once settled, what it pointed at or why it
// failed. A step with neither renders as before.
const toItem = (
    step: AgentActivityStep,
    actions: ToolActivityActions,
): ToolCallItemSource => {
    const base = { id: step.id, status: stepStatus(step) };
    if (step.type === "tool_running") return base;
    const { outcome } = step;
    switch (outcome.type) {
        case "failed": {
            const { reason } = outcome;
            if (reason === undefined) return base;
            const failed = { ...base, errorReason: failureReason(reason) };
            if (reason.type !== "source_content_missing") return failed;
            // The missing source as the row's chip, plus its recovery action.
            const { source } = reason;
            const reupload = actions.reuploadSource;
            const named = {
                ...failed,
                resources: [toResource(source, actions.openResource)],
            };
            return reupload === undefined
                ? named
                : {
                      ...named,
                      onReupload: () => reupload(source.id, source.title),
                  };
        }
        case "completed": {
            const { detail } = outcome;
            if (detail === undefined) return base;
            const resources = detailLinks(detail).map((link) =>
                toResource(link, actions.openResource),
            );
            const line = resultLine(detail);
            const described =
                line === undefined
                    ? { ...base, resources }
                    : { ...base, resources, resultLine: line };
            const download = actions.downloadExport;
            return detail.type === "package_exported" && download !== undefined
                ? {
                      ...described,
                      onDownload: () => download(detail.package.id),
                  }
                : described;
        }
        default:
            return exhaustSwitchCase(outcome);
    }
};

// === Read locators (todo_tool_read_locators) ===
//
// A settled read that names WHICH part of a source it read. Consecutive
// reads of the same source (same unit) merge into ONE row listing the parts
// ("s. 12–15, 45–52, 80 i [källa]") instead of the same chip five times.

type ReadPart = {
    readonly link: ToolResourceLink;
    readonly locator: ToolReadLocator;
};

const readPartOf = (step: AgentActivityStep): ReadPart | undefined => {
    if (step.type !== "tool_done" || step.outcome.type !== "completed")
        return undefined;
    const { detail } = step.outcome;
    return detail?.type === "resource_touched" && detail.locator !== undefined
        ? { link: detail.resource, locator: detail.locator }
        : undefined;
};

const unitOf = (locator: ToolReadLocator): ToolCallReadSpan["unit"] => {
    switch (locator.type) {
        case "page_range":
            return "pages";
        case "section_range":
            return "sections";
        default:
            return exhaustSwitchCase(locator);
    }
};

type Range = { readonly first: number; readonly last: number };

// Sort, then merge overlapping or adjacent ranges (12–13 + 14–15 → 12–15).
const mergeRanges = (
    ranges: readonly [Range, ...Range[]],
): readonly [Range, ...Range[]] => {
    const [head, ...rest] = [...ranges].sort(
        (a, b) => a.first - b.first || a.last - b.last,
    );
    const merged: [Range, ...Range[]] = [head ?? ranges[0]];
    for (const r of rest) {
        const top = merged[merged.length - 1];
        if (top !== undefined && r.first <= top.last + 1) {
            merged[merged.length - 1] = {
                first: top.first,
                last: Math.max(top.last, r.last),
            };
        } else {
            merged.push(r);
        }
    }
    return merged;
};

// A run of reads of one source as one item. A page range opens the source at
// its first page with the whole range marked; a section range opens the source (no section jump yet).
const toReadItem = (
    id: string,
    link: ToolResourceLink,
    locators: readonly [ToolReadLocator, ...ToolReadLocator[]],
    open: ToolActivityActions["openResource"],
): ToolCallItemSource => {
    const unit = unitOf(locators[0]);
    const [r0, ...rs] = mergeRanges(locators);
    const canOpen = open !== undefined && link.kind === "source";
    const ref: ResourceRef = { type: "source", id: link.id, title: link.title };
    const toRange = (r: Range): ToolCallReadRange =>
        canOpen
            ? {
                  ...r,
                  onOpen: () =>
                      open(
                          ref,
                          unit === "pages"
                              ? pageFocusTarget(r.first, r.last)
                              : undefined,
                      ),
              }
            : r;
    return {
        id,
        status: "done",
        resources: [toResource(link, open)],
        readSpan: { unit, ranges: [toRange(r0), ...rs.map(toRange)] },
    };
};

// One group's steps as items: each step its own item, except that a run of
// same-source reads collapses into one read item.
const toItems = (
    steps: readonly [AgentActivityStep, ...AgentActivityStep[]],
    actions: ToolActivityActions,
): readonly [ToolCallItemSource, ...ToolCallItemSource[]] => {
    type Run = {
        readonly id: string;
        readonly part: ReadPart;
        readonly locators: [ToolReadLocator, ...ToolReadLocator[]];
    };
    const slots: (Run | ToolCallItemSource)[] = [];
    for (const step of steps) {
        const part = readPartOf(step);
        const last = slots.at(-1);
        if (
            part !== undefined &&
            last !== undefined &&
            "part" in last &&
            last.part.link.id === part.link.id &&
            unitOf(last.part.locator) === unitOf(part.locator)
        ) {
            last.locators.push(part.locator);
            continue;
        }
        slots.push(
            part === undefined
                ? toItem(step, actions)
                : { id: step.id, part, locators: [part.locator] },
        );
    }
    const toSlotItem = (slot: Run | ToolCallItemSource): ToolCallItemSource =>
        "part" in slot
            ? toReadItem(
                  slot.id,
                  slot.part.link,
                  slot.locators,
                  actions.openResource,
              )
            : slot;
    const [first, ...rest] = slots;
    return first === undefined
        ? [toItem(steps[0], actions)]
        : [toSlotItem(first), ...rest.map(toSlotItem)];
};

// Fold consecutive same-name steps into groups, order-preserving. Pure and
// total: an empty step list yields an empty group list (ToolActivityView
// already renders nothing for that).
const selectToolActivity = (
    steps: readonly AgentActivityStep[],
    actions: ToolActivityActions = {},
): readonly ToolActivityGroupSource[] => {
    const runs: [AgentActivityStep, ...AgentActivityStep[]][] = [];
    for (const step of steps) {
        const last = runs.at(-1);
        if (last !== undefined && last[0].name === step.name) last.push(step);
        else runs.push([step]);
    }
    return runs.map((run) => ({
        id: `${run[0].name}-${run[0].id}`,
        verb: toolStepVerb(run[0].name),
        items: toItems(run, actions),
    }));
};
